import { useQuery } from '@tanstack/react-query';
import { Link, Outlet } from 'react-router-dom';

import { adminApi } from '@/api';
import { Icon } from '@/components/ui';
import { AppShell } from '@/components/shell';
import { NotificationBell } from '@/features/notifications/NotificationBell';
import { useAuth } from '@/hooks/useAuth';
import { paths } from '@/routes/paths';

/**
 * The super admin's menu, in the sections of the product brief: users,
 * internships and recruitment, subscriptions, verification, reports, system --
 * with the overview first, as the student menu puts its dashboard first.
 *
 * Every destination appears exactly once, so only one row is ever lit as
 * "you are here". The queue badges stay on the rows whose queues they count.
 */
const NAV_GROUPS = [
  {
    label: 'Dashboard',
    items: [{ to: paths.admin.overview, label: 'Overview', icon: 'dashboard' }],
  },
  {
    label: 'User management',
    items: [
      { to: paths.admin.students, label: 'Students', icon: 'graduationCap' },
      { to: paths.admin.companies, label: 'Companies', icon: 'building' },
      // Recruiters is switched off from the menu: All users -> Companies
      // filters company people by workspace role instead. The page still
      // exists; restore this line to bring the entry back.
      // { to: paths.admin.recruiters, label: 'Recruiters', icon: 'idBadge' },
      // Every account: activate, deactivate, suspend, ban, sign in as.
      { to: paths.admin.users, label: 'All users', icon: 'team' },
    ],
  },
  {
    label: 'Internship & recruitment',
    items: [
      {
        // Approve or reject listings, remove fraudulent ones.
        to: paths.admin.internships,
        label: 'Internships',
        icon: 'briefcase',
        badge: 'pending_internships',
      },
      // Hidden for now: the entry below opens on the same list, with interviews
      // and offers beside it. The route is still registered.
      // { to: paths.admin.applications, label: 'Applications', icon: 'clipboard' },
      // The recruitment screen, named for its first and default tab:
      // applications, then interviews and offers, across every company.
      { to: paths.admin.recruitment, label: 'Applications', icon: 'funnel' },
      {
        to: paths.admin.reports,
        label: 'Reported content',
        icon: 'flag',
        badge: 'open_reports',
      },
      // Reported conversations are read here.
      { to: paths.admin.messages, label: 'Messages', icon: 'chat' },
    ],
  },
  {
    label: 'Subscription management',
    items: [
      { to: paths.admin.plans, label: 'Plans', icon: 'package' },
      // Assign, upgrade and downgrade.
      { to: paths.admin.subscriptions, label: 'Subscriptions', icon: 'repeat' },
      // Payment history and invoices.
      { to: paths.admin.billing, label: 'Payments & invoices', icon: 'creditCard' },
      { to: paths.admin.coupons, label: 'Coupons & promotions', icon: 'ticket' },
    ],
  },
  {
    label: 'Verification management',
    items: [
      {
        to: paths.admin.verifications,
        label: 'Company verification',
        icon: 'shieldCheck',
        badge: 'pending_verifications',
      },
      // Student identity verification is switched off for now: refusing it
      // limited nothing. Restore this entry to bring the queue back.
      // { to: paths.admin.studentVerifications, label: 'Student verification', icon: 'userCheck' },
      {
        // Certificates (diplomas) and CVs.
        to: paths.admin.documentVerifications,
        label: 'Certificates & CVs',
        icon: 'fileText',
        badge: 'pending_documents',
      },
    ],
  },
  {
    label: 'Reports & analytics',
    items: [
      // Platform statistics, registrations, internships and subscriptions.
      { to: paths.admin.analytics, label: 'Platform analytics', icon: 'chartBar' },
      { to: paths.admin.revenueReport, label: 'Revenue', icon: 'banknote' },
      { to: paths.admin.verificationReport, label: 'Verification report', icon: 'flag' },
      { to: paths.admin.aiReport, label: 'AI usage', icon: 'sparkles' },
    ],
  },
  {
    label: 'System administration',
    items: [
      { to: paths.admin.universities, label: 'Universities', icon: 'landmark' },
      { to: paths.admin.industries, label: 'Industries', icon: 'factory' },
      { to: paths.admin.jobCategories, label: 'Job categories', icon: 'tag' },
      { to: paths.admin.skills, label: 'Skills', icon: 'zap' },
      { to: paths.admin.integrations, label: 'Third-party APIs', icon: 'plug' },
      { to: paths.admin.announcements, label: 'Announcements', icon: 'bell' },
      // Hidden from the menu for now; the page still works at its URL.
      // { to: paths.admin.audit, label: 'Audit log', icon: 'history' },
    ],
  },
];

export function AdminLayout() {
  const { user, signOut } = useAuth();

  // Cheap enough to keep fresh; the queue counts are the point of the rail.
  const { data: overview } = useQuery({
    queryKey: ['admin-overview'],
    queryFn: adminApi.getOverview,
    refetchInterval: 60_000,
  });

  const pendingTotal =
    (overview?.pending_verifications ?? 0) +
    (overview?.pending_internships ?? 0) +
    (overview?.pending_documents ?? 0);

  return (
    <AppShell
      nav={NAV_GROUPS}
      counts={overview}
      sectionLabel="Admin"
      roleLabel="Administrator"
      user={user}
      profileTo={paths.admin.profile}
      accountMenu={[
        { to: paths.admin.profile, label: 'My profile', icon: 'user' },
        { to: `${paths.admin.profile}#password`, label: 'Change password', icon: 'shieldCheck' },
        { to: paths.notifications, label: 'Notifications', icon: 'bell' },
      ]}
      onSignOut={() => void signOut()}
      headerRight={
        <>
          {pendingTotal > 0 && (
            <Link
              to={paths.admin.overview}
              className="bg-warning-50 text-warning-800 ring-warning-100 hover:bg-warning-100 hidden items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold ring-1 transition ring-inset sm:inline-flex"
            >
              <Icon name="clock" size={14} />
              {pendingTotal} waiting
            </Link>
          )}
          <NotificationBell />
        </>
      }
    >
      <Outlet />
    </AppShell>
  );
}
